import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Instagram, Loader2, Mail, MapPin, MessageCircle, Save, Settings2, Youtube } from "lucide-react";
import api from "@/lib/api";
import { Label, SectionTitle, formatApiError, solidBtn } from "./shared";

const FIELDS = [
  { key: "instagram", label: "Instagram URL", icon: Instagram, placeholder: "https://instagram.com/yourhandle" },
  { key: "youtube", label: "YouTube Channel URL", icon: Youtube, placeholder: "https://youtube.com/@yourchannel" },
  { key: "whatsapp", label: "WhatsApp Number (with country code)", icon: MessageCircle, placeholder: "+91 98765 43210" },
  { key: "email", label: "Public Email", icon: Mail, placeholder: "hello@anshagarwalfilms.com" },
  { key: "location", label: "Studios / Location line", icon: MapPin, placeholder: "Mumbai · New Delhi · Worldwide" },
];

export const SiteSettings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      toast.success("Contact details updated — live on the site now.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <section className="border border-line bg-coal p-6 sm:p-8 flex justify-center">
        <Loader2 size={18} className="animate-spin text-amber" />
      </section>
    );
  }

  return (
    <section data-testid="admin-settings-section" className="border border-line bg-coal p-6 sm:p-8 max-w-3xl">
      <SectionTitle icon={Settings2}>Live Contact Details</SectionTitle>
      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-mute mt-2">
        Shown in the Contact section and footer — leave a field blank to hide it
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 mt-6">
        {FIELDS.map((f) => {
          const Icon = f.icon;
          return (
            <div key={f.key} className={f.key === "location" ? "sm:col-span-2" : ""}>
              <Label>
                <span className="inline-flex items-center gap-1.5"><Icon size={10} className="text-amber" /> {f.label}</span>
              </Label>
              <input
                data-testid={`admin-settings-input-${f.key}`}
                className="field mt-1"
                placeholder={f.placeholder}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              />
            </div>
          );
        })}
      </div>
      <button data-testid="admin-settings-save-button" onClick={save} disabled={saving} className={`${solidBtn} mt-7 w-full sm:w-auto px-10`}>
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {saving ? "Saving…" : "Save Contact Details"}
      </button>
    </section>
  );
};
